import { useMemo, useState } from "react";
import {
  Building2,
  Crown,
  Loader2,
  Pencil,
  Plus,
  ShieldCheck,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import {
  useRemoveTeamMember,
  useSyncTeamAccess,
  useTeam,
  useUpdateTeamMember,
} from "../hooks/useTeam";
import { AddTeamMemberDrawer } from "./AddTeamMemberDrawer";
import type { TeamEntityType, TeamMember } from "../types";

const CARD_BORDER = "hsl(210,30%,85%)";

function isAdminTier(roles: { name: string }[] | undefined): boolean {
  if (!roles) return false;
  return roles.some((r) => {
    const name = r.name.toLowerCase();
    return (
      name === "super admin" ||
      name === "managing partner" ||
      name.includes("admin")
    );
  });
}

function initials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

type Props = {
  entityType: TeamEntityType;
  entityId: string;
};

export function EntityTeamTab({ entityType, entityId }: Props) {
  const { user } = useAuth();
  const { data: members, isLoading } = useTeam(entityType, entityId);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<TeamMember | null>(null);

  const isAdmin = isAdminTier(user?.roles);

  const sortedMembers = members ?? [];
  const lead = sortedMembers.find((m) => m.isLead);
  const internal = useMemo(
    () =>
      sortedMembers.filter(
        (m) => m.kind === "internal" && m.id !== lead?.id,
      ),
    [sortedMembers, lead],
  );
  const external = useMemo(
    () =>
      sortedMembers.filter(
        (m) => m.kind === "external" && m.id !== lead?.id,
      ),
    [sortedMembers, lead],
  );

  const removeMember = useRemoveTeamMember(entityType, entityId);
  const syncAccess = useSyncTeamAccess();

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }

  function handleRemove(member: TeamMember) {
    setRemoveTarget(member);
  }

  async function confirmRemove() {
    if (!removeTarget) return;
    await removeMember.mutateAsync(removeTarget.id);
  }

  async function handleSync() {
    setSyncing(true);
    try {
      const res = await syncAccess.mutateAsync({ entityType, entityId });
      setSyncOpen(false);
      showToast(
        `${res.created} assignment${res.created === 1 ? "" : "s"} created. ${res.alreadyExisted} already existed.`,
      );
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : "Failed to sync access",
      );
    } finally {
      setSyncing(false);
    }
  }

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center rounded-xl border bg-card py-16 text-sm text-muted-foreground"
        style={{ borderColor: CARD_BORDER }}
      >
        <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
        Loading team…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {toast && (
        <div
          className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium shadow-lg"
          style={{
            borderColor: "hsl(142,55%,80%)",
            color: "hsl(142,72%,28%)",
          }}
        >
          {toast}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {sortedMembers.length === 0
            ? "No team members yet."
            : `${sortedMembers.length} member${sortedMembers.length === 1 ? "" : "s"}`}
        </div>
        <Button size="sm" onClick={() => setDrawerOpen(true)}>
          <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
          Add Member
        </Button>
      </div>

      {lead && (
        <Section
          title="Lead"
          icon={<Crown className="h-3.5 w-3.5" strokeWidth={1.5} />}
        >
          <MemberRow
            entityType={entityType}
            entityId={entityId}
            member={lead}
            isEditing={editingId === lead.id}
            onEdit={() => setEditingId(lead.id)}
            onCancelEdit={() => setEditingId(null)}
            onRemove={() => handleRemove(lead)}
          />
        </Section>
      )}

      {internal.length > 0 && (
        <Section title={`Internal Members (${internal.length})`}>
          <div className="space-y-2">
            {internal.map((m) => (
              <MemberRow
                key={m.id}
                entityType={entityType}
                entityId={entityId}
                member={m}
                isEditing={editingId === m.id}
                onEdit={() => setEditingId(m.id)}
                onCancelEdit={() => setEditingId(null)}
                onRemove={() => handleRemove(m)}
              />
            ))}
          </div>
        </Section>
      )}

      {external.length > 0 && (
        <Section title={`External Members (${external.length})`}>
          <div className="space-y-2">
            {external.map((m) => (
              <MemberRow
                key={m.id}
                entityType={entityType}
                entityId={entityId}
                member={m}
                isEditing={editingId === m.id}
                onEdit={() => setEditingId(m.id)}
                onCancelEdit={() => setEditingId(null)}
                onRemove={() => handleRemove(m)}
              />
            ))}
          </div>
        </Section>
      )}

      {isAdmin && sortedMembers.some((m) => m.kind === "internal") && (
        <div className="border-t pt-4" style={{ borderColor: CARD_BORDER }}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSyncOpen(true)}
            className="text-[hsl(210,68%,40%)]"
          >
            <ShieldCheck className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
            Sync internal team to access assignments
          </Button>
          <p className="mt-1.5 text-[11px] text-muted-foreground">
            Mirrors current internal team members into Entity Assignments —
            grants them access to this entity. Idempotent. External members
            are unaffected.
          </p>
        </div>
      )}

      <AddTeamMemberDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        entityType={entityType}
        entityId={entityId}
        existingMembers={sortedMembers}
      />

      {syncOpen && (
        <SyncConfirmModal
          onCancel={() => setSyncOpen(false)}
          onConfirm={handleSync}
          submitting={syncing}
        />
      )}

      <ConfirmDialog
        open={removeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRemoveTarget(null);
        }}
        title="Remove from team?"
        description={(() => {
          if (!removeTarget) return "";
          const label =
            removeTarget.userName ?? removeTarget.contactName ?? "this member";
          return `Are you sure you want to remove '${label}' from the team?`;
        })()}
        confirmLabel="Remove"
        onConfirm={confirmRemove}
      />
    </div>
  );
}

// ── Subcomponents ─────────────────────────────────────────────

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
        {icon}
        {title}
      </div>
      {children}
    </div>
  );
}

function MemberRow({
  entityType,
  entityId,
  member,
  isEditing,
  onEdit,
  onCancelEdit,
  onRemove,
}: {
  entityType: TeamEntityType;
  entityId: string;
  member: TeamMember;
  isEditing: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onRemove: () => void;
}) {
  const update = useUpdateTeamMember(entityType, entityId);
  const rolesQuery = useCategoryItems("team.role");
  const roles = rolesQuery.data ?? [];

  const [roleValue, setRoleValue] = useState(member.roleValue ?? "");
  const [isLead, setIsLead] = useState(member.isLead);

  async function handleSave() {
    await update.mutateAsync({
      id: member.id,
      payload: {
        roleValue: roleValue || null,
        isLead,
      },
    });
    onCancelEdit();
  }

  const name = member.userName ?? member.contactName ?? "(unknown)";
  const subtitleParts: string[] = [];
  if (member.kind === "internal") {
    if (member.userEmail) subtitleParts.push(member.userEmail);
  } else {
    if (member.contactCompanyName) subtitleParts.push(member.contactCompanyName);
    if (member.contactEmail) subtitleParts.push(member.contactEmail);
  }
  const tint =
    member.kind === "internal"
      ? { bg: "hsl(210,55%,92%)", fg: "hsl(210,100%,28%)" }
      : { bg: "hsl(38,92%,92%)", fg: "hsl(38,92%,30%)" };

  return (
    <div
      className="flex items-center gap-3 rounded-lg border bg-card px-4 py-2.5"
      style={{ borderColor: CARD_BORDER }}
    >
      <div
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold"
        style={{ background: tint.bg, color: tint.fg }}
      >
        {initials(name)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-foreground">
            {name}
          </span>
          {member.isLead && (
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium"
              style={{
                background: "hsl(45,90%,92%)",
                color: "hsl(40,90%,32%)",
              }}
            >
              <Crown className="h-2.5 w-2.5" strokeWidth={2} />
              Lead
            </span>
          )}
          {member.roleLabel && !isEditing && (
            <span className="text-xs text-muted-foreground">
              · {member.roleLabel}
            </span>
          )}
          {member.kind === "external" && member.contactCompanyName && (
            <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
              <Building2 className="h-3 w-3" strokeWidth={1.5} />
              {member.contactCompanyName}
            </span>
          )}
        </div>
        {!isEditing && subtitleParts.length > 0 && (
          <div className="truncate text-[11px] text-muted-foreground">
            {subtitleParts.join(" · ")}
          </div>
        )}
        {isEditing && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Select
              value={roleValue}
              onChange={(e) => setRoleValue(e.target.value)}
              className="h-8 w-44 text-xs"
            >
              <option value="">— No role —</option>
              {roles
                .filter((r) => r.isActive || r.value === roleValue)
                .map((r) => (
                  <option key={r.id} value={r.value}>
                    {r.label}
                  </option>
                ))}
            </Select>
            <label className="flex items-center gap-1.5 text-xs">
              <input
                type="checkbox"
                checked={isLead}
                onChange={(e) => setIsLead(e.target.checked)}
              />
              Lead
            </label>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={update.isPending}
              className="h-7 text-xs"
            >
              {update.isPending && (
                <Loader2
                  className="mr-1 h-3 w-3 animate-spin"
                  strokeWidth={1.5}
                />
              )}
              Save
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onCancelEdit}
              className="h-7 text-xs"
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
      {!isEditing && (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onEdit}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
            aria-label="Edit"
          >
            <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(0,84%,95%)] hover:text-destructive"
            aria-label="Remove"
          >
            <X className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
        </div>
      )}
    </div>
  );
}

function SyncConfirmModal({
  onCancel,
  onConfirm,
  submitting,
}: {
  onCancel: () => void;
  onConfirm: () => void;
  submitting: boolean;
}) {
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30">
      <div
        className="w-[440px] rounded-xl border bg-white shadow-2xl"
        style={{ borderColor: CARD_BORDER }}
      >
        <div
          className="flex items-center justify-between border-b px-5 py-3"
          style={{ borderColor: CARD_BORDER }}
        >
          <h3 className="text-sm font-semibold">Sync Team to Access</h3>
          <button
            type="button"
            onClick={onCancel}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>
        <div className="p-5 text-sm text-foreground">
          This will create entity assignments for all internal team members on
          this entity, granting them access. External members are unaffected.
          Existing assignments are not duplicated. Continue?
        </div>
        <div
          className="flex items-center justify-end gap-2 border-t px-5 py-3"
          style={{ borderColor: CARD_BORDER }}
        >
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={onConfirm} disabled={submitting}>
            {submitting && (
              <Loader2
                className="mr-1 h-3.5 w-3.5 animate-spin"
                strokeWidth={1.5}
              />
            )}
            Sync access
          </Button>
        </div>
      </div>
    </div>
  );
}
